import React, { Component } from "react";
import ReactDOM from "react-dom";
import { alert, t } from "../../../components/CustomComponent.js";
import InfoTooltip from "./InfoTooltip.js";
import {
  LABEL_MAX,
  TYPE_VALUES,
  canMove,
  countItems,
  createItem,
  createSection,
  defaultLabels,
  hasExtra,
  hasUnit,
  isInvalidRange,
  isToggle2,
  isToggle3,
  isUnitMissing,
  limitDecimals,
  locate,
  moveByDirection,
  moveTo,
  ownsList,
  selectValue,
  typeLabel,
} from "./checklistTree.js";
import "./checklist-tree.css";

const requiredTip = (type) => (isToggle3(type) ? t("ct_req_tip_toggle3") : isToggle2(type) ? t("ct_req_tip_toggle2") : t("ct_req_tip_other"));

const prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const flashEl = (el) => {
  if (!el) return;
  el.classList.remove("flash");
  // บังคับ reflow ให้ animation เล่นซ้ำได้
  void el.offsetWidth;
  el.classList.add("flash");
  setTimeout(() => el.classList.remove("flash"), 1200);
};

/**
 * ต้นไม้ Section / รายการ ของเทมเพลต PM Checklist (ซ้อนไม่จำกัดชั้น ลากวาง + โหมดลูกศร)
 * props: tree (array ของ node จาก checklistTree.js — แก้ในที่เดิมแล้วเรียก onChange ด้วย array ใหม่),
 *        onChange(tree), headerExtra (node วางต่อท้ายหัว "Checklist Items" เช่นช่อง Remark)
 */
export default class ChecklistTreeEditor extends Component {
  state = { moving: null, live: "" };

  root = React.createRef();
  line = null;
  D = null; // สถานะการลากด้วยเมาส์/นิ้ว
  raf = null;
  pending = null; // งานหลัง render: เลื่อน/กระพริบ/โฟกัสของที่เพิ่งเพิ่มหรือเพิ่งย้าย

  componentDidMount() {
    this.line = document.createElement("div");
    this.line.className = "ct-dropline";
    document.body.appendChild(this.line);
    document.addEventListener("keydown", this.onDocKeyDown);
    document.addEventListener("pointerdown", this.onDocPointerDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onDocKeyDown);
    document.removeEventListener("pointerdown", this.onDocPointerDown);
    cancelAnimationFrame(this.raf);
    this.cleanupDrag();
    this.line?.remove();
  }

  componentDidUpdate() {
    const p = this.pending;
    if (!p) return;
    this.pending = null;
    const root = this.root.current;
    if (!root) return;
    const behavior = prefersReducedMotion() ? "auto" : "smooth";
    if (p.focus === "input") {
      const input = root.querySelector(`[data-ct-focus="${p.id}"]`);
      const box = input && (input.closest(".ct-item") || input.closest(".ct-shead"));
      if (box) {
        box.scrollIntoView({ block: "nearest", behavior });
        flashEl(box);
        input.focus({ preventScroll: true });
      }
    } else {
      const handle = root.querySelector(`[data-ct-handle="${p.id}"]`);
      const box = handle && (handle.closest(".ct-item") || handle.closest(".ct-shead"));
      if (box) {
        flashEl(box);
        handle.focus({ preventScroll: true });
        box.scrollIntoView({ block: "nearest" });
      }
    }
  }

  tree = () => this.props.tree;
  commit = () => this.props.onChange([...this.props.tree]);

  labelOf = (n) =>
    n.kind === "section"
      ? `Section "${n.name || t("ct_unnamed")}"`
      : `"${n.description || t("ct_new_item")}"`;

  // ---------------- เพิ่ม / ลบ ----------------
  // Add Item ใน Section: แทรกต่อรายการสุดท้ายก่อน Section ย่อยตัวแรก · ระดับบนสุดต่อท้าย
  addItem = (sec) => {
    const it = createItem();
    if (sec) {
      sec.open = true;
      const firstSub = sec.children.findIndex((x) => x.kind === "section");
      if (firstSub < 0) sec.children.push(it);
      else sec.children.splice(firstSub, 0, it);
    } else {
      this.props.tree.push(it);
    }
    this.pending = { id: it.id, focus: "input" };
    this.commit();
  };

  addSection = (sec) => {
    const sc = createSection();
    if (sec) {
      sec.open = true;
      sec.children.push(sc);
    } else {
      this.props.tree.push(sc);
    }
    this.pending = { id: sc.id, focus: "input" };
    this.commit();
  };

  removeNode = (node) => {
    const p = locate(this.tree(), node.id);
    if (!p) return;
    p.list.splice(p.index, 1);
    this.commit();
  };

  removeSection = async (sec, num) => {
    const total = countItems(sec.children);
    const hasContent = sec.children.length > 0;
    if (hasContent) {
      const ok = await alert(
        "",
        "warning",
        t("ct_delete_section_title"),
        `${t("ct_delete_section_confirm")} Section ${num} "${sec.name || t("ct_unnamed")}" ${t("ct_delete_section_confirm_suffix")} (${total} ${t("items")})`,
        t("confirm"),
        t("cancel")
      );
      if (!ok) return;
    }
    if (this.state.moving === sec.id) this.setState({ moving: null });
    this.removeNode(sec);
  };

  toggleFold = (sec) => {
    sec.open = !sec.open;
    this.commit();
  };

  update = (node, patch) => {
    Object.assign(node, patch);
    this.commit();
  };

  changeType = (node, value) => {
    const patch = { input_type: value, label_left: "", label_right: "" };
    if (value !== "measurement") Object.assign(patch, { min: "", max: "" });
    if (!hasUnit(value)) patch.unit = "";
    this.update(node, patch);
  };

  // ---------------- ย้าย (ใช้ร่วมกันทั้งลากวางและลูกศร) ----------------
  afterMove = (id) => {
    const tree = this.tree();
    let q = locate(tree, id);
    const p = q;
    // กางทุก Section บรรพบุรุษของที่ย้าย
    while (q && q.parent) {
      q.parent.open = true;
      q = locate(tree, q.parent.id);
    }
    this.pending = { id, focus: "handle" };
    const where = p.parent
      ? `${t("ct_live_in_section")} "${p.parent.name || t("ct_unnamed")}" ${t("ct_live_position")} ${p.index + 1}`
      : `${t("ct_live_top")} ${t("ct_live_position")} ${p.index + 1}`;
    this.setState({ live: `${t("ct_live_moved")} ${this.labelOf(p.node)} ${where}` });
    this.commit();
  };

  // ---------------- โหมดย้ายด้วยคลิก ⋮⋮ + ลูกศร ----------------
  setMoving = (id) => {
    if (id == null) {
      this.setState({ moving: null, live: t("ct_live_exit") });
      return;
    }
    const p = locate(this.tree(), id);
    if (!p) return;
    this.setState({ moving: id, live: `${t("ct_live_selected")} ${this.labelOf(p.node)} ${t("ct_live_selected_hint")}` });
  };

  doMove = (dir) => {
    const id = this.state.moving;
    if (id == null) return;
    if (moveByDirection(this.tree(), id, dir)) this.afterMove(id);
  };

  onHandleKeyDown = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.setMoving(this.state.moving === id ? null : id);
    }
  };

  onDocKeyDown = (e) => {
    if (e.key === "Escape" && this.D && this.D.started) {
      this.endDrag(false);
      return;
    }
    if (this.state.moving == null) return;
    if (e.key === "Escape") {
      e.preventDefault();
      this.setMoving(null);
      return;
    }
    const map = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
    if (map[e.key] && !/^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName)) {
      e.preventDefault();
      this.doMove(map[e.key]);
    }
  };

  // คลิกที่อื่น (นอกแถบและนอก ⋮⋮) = ออกจากโหมดย้าย
  onDocPointerDown = (e) => {
    if (this.state.moving != null && !e.target.closest(".ct-drag") && !e.target.closest(".ct-movebar")) this.setMoving(null);
  };

  // ---------------- ลากด้วยเมาส์/นิ้ว ----------------
  onPointerDown = (e) => {
    const h = e.target.closest(".ct-drag");
    if (!h || e.button > 0) return;
    e.preventDefault();
    const id = h.dataset.ctHandle;
    const p = locate(this.tree(), id);
    if (!p) return;
    this.D = { id, node: p.node, h, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, started: false, target: null };
    try {
      h.setPointerCapture(e.pointerId);
    } catch (err) {
      // เบราว์เซอร์ที่ไม่รองรับ capture ยังลากได้ภายในกรอบ
    }
  };

  onPointerMove = (e) => {
    const D = this.D;
    if (!D) return;
    D.x = e.clientX;
    D.y = e.clientY;
    if (!D.started) {
      if (Math.hypot(e.clientX - D.x0, e.clientY - D.y0) < 5) return;
      this.startDrag();
      this.autoScroll();
    }
    D.ghost.style.left = `${e.clientX}px`;
    D.ghost.style.top = `${e.clientY}px`;
    D.target = this.computeTarget(e.clientX, e.clientY);
  };

  onPointerUp = () => this.endDrag(true);
  onPointerCancel = () => this.endDrag(false);

  startDrag = () => {
    const D = this.D;
    D.started = true;
    if (this.state.moving != null) this.setState({ moving: null });
    document.body.classList.add("ct-is-dragging");
    const root = this.root.current;
    const block = D.node.kind === "section" ? root.querySelector(`.ct-section[data-ct-id="${D.id}"]`) : D.h.closest(".ct-item");
    block.classList.add("ct-dragging");
    D.block = block;
    const g = document.createElement("div");
    g.className = "ct-ghost";
    g.textContent =
      "⋮⋮ " + (D.node.kind === "section" ? `${D.node.name || "Section"} (${countItems(D.node.children)} ${t("items")})` : D.node.description || t("ct_new_item"));
    document.body.appendChild(g);
    D.ghost = g;
  };

  cleanupDrag = () => {
    document.body.classList.remove("ct-is-dragging");
    document.querySelectorAll(".ct-ghost").forEach((g) => g.remove());
    if (this.line) this.line.style.display = "none";
  };

  showLine = (x, y, w) => {
    const l = this.line;
    l.style.display = "block";
    l.style.left = `${x}px`;
    l.style.top = `${y - 1.5}px`;
    l.style.width = `${w}px`;
  };

  clearMarks = () => {
    if (this.line) this.line.style.display = "none";
    this.root.current?.querySelectorAll(".drop-into").forEach((x) => x.classList.remove("drop-into"));
  };

  listOfContainer = (c) => {
    if (c.classList.contains("ct-items")) return this.tree();
    return locate(this.tree(), c.closest(".ct-section").dataset.ctId).node.children;
  };

  computeTarget = (x, y) => {
    const D = this.D;
    const root = this.root.current;
    const tree = this.tree();
    this.clearMarks();
    D.ghost.style.display = "none";
    const el = document.elementFromPoint(x, y);
    D.ghost.style.display = "";
    const itemsBox = root.querySelector(".ct-items");
    if (!el || !itemsBox.contains(el)) return null;
    if (D.block.contains(el)) return null; // ห้ามวางในตัวเอง
    const item = el.closest(".ct-item");
    const head = el.closest(".ct-shead");
    if (item) {
      const p = locate(tree, item.dataset.ctId);
      const r = item.getBoundingClientRect();
      const before = y < r.top + r.height / 2;
      this.showLine(r.left, before ? r.top - 4 : r.bottom + 4, r.width);
      return { list: p.list, index: p.index + (before ? 0 : 1) };
    }
    if (head) {
      const sec = locate(tree, head.closest(".ct-section").dataset.ctId);
      const r = head.getBoundingClientRect();
      if (y < r.top + r.height * 0.35) {
        this.showLine(r.left, r.top - 4, r.width);
        return { list: sec.list, index: sec.index };
      }
      if (D.node.kind === "section" && (sec.node === D.node || ownsList(D.node, sec.node.children))) return null;
      head.classList.add("drop-into");
      return { list: sec.node.children, index: 0, into: sec.node };
    }
    const c = el.closest(".ct-sbody, .ct-items");
    if (!c) return null;
    const kids = [...c.children].filter((k) => k.matches(".ct-item,.ct-section") && k !== D.block);
    const list = this.listOfContainer(c);
    if (D.node.kind === "section" && (list === D.node.children || ownsList(D.node, list))) return null;
    const nxt = kids.find((k) => {
      const r = k.getBoundingClientRect();
      return y < r.top + r.height / 2;
    });
    const cr = (nxt || kids[kids.length - 1] || c).getBoundingClientRect();
    if (nxt) {
      this.showLine(cr.left, cr.top - 4, cr.width);
      return { list, index: list.indexOf(locate(tree, nxt.dataset.ctId).node) };
    }
    this.showLine(cr.left, (kids.length ? cr.bottom : cr.top) + 4, cr.width);
    return { list, index: list.length };
  };

  // ตัวที่เลื่อนได้ใกล้ที่สุด (modal เลื่อนใน .modal ไม่ใช่ window)
  getScroller = () => {
    let el = this.root.current;
    while (el && el !== document.body) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  };

  autoScroll = () => {
    const D = this.D;
    if (!D || !D.started) return;
    const sc = this.getScroller();
    const top = sc ? sc.getBoundingClientRect().top : 0;
    const bottom = sc ? sc.getBoundingClientRect().bottom : window.innerHeight;
    const m = 70;
    let dy = 0;
    if (D.y < top + m) dy = -Math.ceil((top + m - D.y) / 6);
    else if (D.y > bottom - m) dy = Math.ceil((D.y - (bottom - m)) / 6);
    if (dy) {
      if (sc) sc.scrollTop += dy;
      else window.scrollBy(0, dy);
      D.target = this.computeTarget(D.x, D.y);
    }
    this.raf = requestAnimationFrame(this.autoScroll);
  };

  endDrag = (commit) => {
    const d = this.D;
    if (!d) return;
    this.D = null;
    cancelAnimationFrame(this.raf);
    this.clearMarks();
    document.body.classList.remove("ct-is-dragging");
    d.ghost && d.ghost.remove();
    d.block && d.block.classList.remove("ct-dragging");
    if (commit && !d.started) {
      // คลิกเฉย ๆ ที่ ⋮⋮ = เข้า/ออกโหมดลูกศร
      this.setMoving(this.state.moving === d.id ? null : d.id);
      return;
    }
    if (commit && d.started && d.target) {
      if (d.target.into) d.target.into.open = true;
      if (moveTo(this.tree(), d.id, d.target.list, d.target.index)) this.afterMove(d.id);
    }
  };

  // ---------------- render ----------------
  renderItem = (it, num) => {
    const type = it.input_type || "check";
    const sel = selectValue(type);
    const extra = hasExtra(type);
    const [dl, dr] = defaultLabels(type);
    const moving = this.state.moving === it.id;
    const bad = isInvalidRange(it);
    const unitBad = isUnitMissing(it);
    const name = it.description || t("ct_new_item");
    return (
      <div key={it.id} className={`ct-item${extra ? " has-x" : ""}${moving ? " moving" : ""}`} data-ct-id={it.id} data-type={sel}>
        <button
          type="button"
          className="ct-drag"
          data-ct-handle={it.id}
          aria-label={`${t("ct_move_item_aria")} ${name}`}
          aria-pressed={moving}
          aria-roledescription={t("ct_drag_role")}
          onKeyDown={(e) => this.onHandleKeyDown(e, it.id)}
        >
          ⋮⋮
        </button>
        {num ? (
          <div className="ct-namewrap">
            <span className="ct-inum">{num}</span>
            <input
              className="ct-name"
              type="text"
              maxLength={500}
              data-ct-focus={it.id}
              aria-label={`${t("ct_item_name_aria")} ${num}`}
              placeholder={t("ct_item_placeholder")}
              value={it.description}
              onChange={(e) => this.update(it, { description: e.target.value })}
            />
          </div>
        ) : (
          <input
            className="ct-name"
            type="text"
            maxLength={500}
            data-ct-focus={it.id}
            aria-label={t("ct_item_name_aria")}
            placeholder={t("ct_item_placeholder")}
            value={it.description}
            onChange={(e) => this.update(it, { description: e.target.value })}
          />
        )}
        <select className="ct-typ" aria-label={t("ct_type_aria")} value={sel} onChange={(e) => this.changeType(it, e.target.value)}>
          {TYPE_VALUES.map((v) => (
            <option key={v} value={v}>
              {typeLabel(v)}
            </option>
          ))}
        </select>

        {type === "measurement" && (
          <div className="ct-meas">
            <input
              className="ct-unit"
              type="text"
              maxLength={100}
              aria-label={t("ct_unit_aria")}
              aria-required="true"
              aria-invalid={unitBad}
              placeholder={t("ct_unit_placeholder")}
              value={it.unit}
              onChange={(e) => this.update(it, { unit: e.target.value })}
            />
            <span className="mm">
              <label htmlFor={`ct-min-${it.id}`}>{t("min_label")}</label>
              <input
                id={`ct-min-${it.id}`}
                className="ct-min"
                type="number"
                inputMode="decimal"
                step="0.01"
                aria-invalid={bad}
                placeholder={t("ct_not_set")}
                title={t("ct_min_title")}
                value={it.min}
                onChange={(e) => this.update(it, { min: limitDecimals(e.target.value) })}
              />
            </span>
            <span className="mm">
              <label htmlFor={`ct-max-${it.id}`}>{t("max_label")}</label>
              <input
                id={`ct-max-${it.id}`}
                className="ct-max"
                type="number"
                inputMode="decimal"
                step="0.01"
                aria-invalid={bad}
                placeholder={t("ct_not_set")}
                title={t("ct_max_title")}
                value={it.max}
                onChange={(e) => this.update(it, { max: limitDecimals(e.target.value) })}
              />
            </span>
            {bad && (
              <span className="ct-rangewarn" role="alert">
                {t("checklist_min_max_invalid")}
              </span>
            )}
          </div>
        )}

        {(isToggle3(type) || isToggle2(type)) && (
          <div className="ct-opts">
            <span className="side">
              <label htmlFor={`ct-lft-${it.id}`}>{t("ct_side_left")}</label>
              <input
                id={`ct-lft-${it.id}`}
                className={isToggle3(type) ? "bad" : "neutral"}
                type="text"
                maxLength={LABEL_MAX}
                autoComplete="off"
                placeholder={dl}
                title={t(isToggle3(type) ? "ct_label_left_title_red" : "ct_label_left_title_white")}
                value={it.label_left}
                onChange={(e) => this.update(it, { label_left: e.target.value })}
              />
            </span>
            <span className="side">
              <label htmlFor={`ct-rgt-${it.id}`}>{t("ct_side_right")}</label>
              <input
                id={`ct-rgt-${it.id}`}
                className="good"
                type="text"
                maxLength={LABEL_MAX}
                autoComplete="off"
                placeholder={dr}
                title={t("ct_label_right_title")}
                value={it.label_right}
                onChange={(e) => this.update(it, { label_right: e.target.value })}
              />
            </span>
          </div>
        )}

        {type === "qty" && (
          <div className="ct-opts qx">
            <input
              className="ct-unit"
              type="text"
              maxLength={100}
              aria-label={t("ct_unit_aria")}
              aria-required="true"
              aria-invalid={unitBad}
              placeholder={t("ct_unit_placeholder")}
              value={it.unit}
              onChange={(e) => this.update(it, { unit: e.target.value })}
            />
          </div>
        )}

        <div className="ct-reqcell">
          <label className={`ct-chk${it.is_required ? " on" : ""}`} htmlFor={`ct-req-${it.id}`}>
            <input id={`ct-req-${it.id}`} type="checkbox" checked={!!it.is_required} onChange={(e) => this.update(it, { is_required: e.target.checked })} />
            {t("required_label")}
          </label>
          <InfoTooltip width="230px">{requiredTip(type)}</InfoTooltip>
        </div>
        <button type="button" className="ct-del" aria-label={t("ct_delete_item")} onClick={() => this.removeNode(it)}>
          ✕
        </button>
      </div>
    );
  };

  renderSection = (sec, num) => {
    const n = countItems(sec.children);
    const moving = this.state.moving === sec.id;
    return (
      <div key={sec.id} className={`ct-section${sec.open ? "" : " closed"}`} data-ct-id={sec.id}>
        <div className={`ct-shead${moving ? " moving" : ""}`}>
          <button
            type="button"
            className="ct-drag"
            data-ct-handle={sec.id}
            aria-label={`${t("ct_move_section_aria")} ${num}`}
            aria-pressed={moving}
            aria-roledescription={t("ct_drag_role")}
            onKeyDown={(e) => this.onHandleKeyDown(e, sec.id)}
          >
            ⋮⋮
          </button>
          <span className="ct-snum">{num}</span>
          <input
            className="ct-sname"
            type="text"
            maxLength={200}
            data-ct-focus={sec.id}
            aria-label={`${t("ct_section_name_aria")} ${num}`}
            placeholder={t("ct_section_placeholder")}
            value={sec.name}
            onChange={(e) => this.update(sec, { name: e.target.value })}
          />
          <span className="ct-scount">
            {n} {t("items")}
          </span>
          <div className="ct-sadd">
            <button type="button" className="ct-add sec sm" aria-label={`${t("ct_add_sub_section_aria")} ${num}`} onClick={() => this.addSection(sec)}>
              {t("ct_add_section")}
            </button>
            <button type="button" className="ct-add sm" aria-label={`${t("ct_add_item_in_aria")} ${num}`} onClick={() => this.addItem(sec)}>
              {t("ct_add_item")}
            </button>
          </div>
          <button type="button" className="ct-fold" aria-label={`${t("ct_fold_aria")} ${num}`} aria-expanded={sec.open} onClick={() => this.toggleFold(sec)}>
            ▾
          </button>
          <button type="button" className="ct-del" aria-label={`${t("ct_delete_section")} ${num}`} onClick={() => this.removeSection(sec, num)}>
            ✕
          </button>
        </div>
        <div className="ct-sbody">
          {this.renderList(sec.children, `${num}.`)}
          {!sec.children.length && <div className="ct-sempty">{t("ct_section_empty")}</div>}
          <div className="ct-send" />
        </div>
      </div>
    );
  };

  renderList = (list, prefix) => {
    let k = 0;
    // ระดับบนสุด (prefix ว่าง): item ใช้ running number ร่วมกับ section · item ใต้ Section ไม่มีเลข
    return list.map((node) => {
      if (node.kind === "section") return this.renderSection(node, `${prefix}${(k += 1)}`);
      return this.renderItem(node, prefix === "" ? String((k += 1)) : "");
    });
  };

  renderMoveBar = () => {
    const { moving } = this.state;
    const p = moving != null ? locate(this.tree(), moving) : null;
    if (!p) return null;
    const c = canMove(this.tree(), moving);
    return ReactDOM.createPortal(
      <div className="ct-movebar" role="toolbar" aria-label={t("ct_move_toolbar")}>
        <span className="mvname">
          {t("ct_moving")} {this.labelOf(p.node)}
        </span>
        <button type="button" disabled={!c.up} aria-label={t("ct_move_up")} onClick={() => this.doMove("up")}>
          ↑
        </button>
        <button type="button" disabled={!c.down} aria-label={t("ct_move_down")} onClick={() => this.doMove("down")}>
          ↓
        </button>
        <button type="button" disabled={!c.left} aria-label={t("ct_move_out")} onClick={() => this.doMove("left")}>
          ←
        </button>
        <button type="button" disabled={!c.right} aria-label={t("ct_move_in")} onClick={() => this.doMove("right")}>
          →
        </button>
        <button type="button" className="mvdone" onClick={() => this.setMoving(null)}>
          {t("ct_move_done")}
        </button>
      </div>,
      document.body
    );
  };

  render() {
    const { tree, headerExtra } = this.props;
    return (
      <div
        className="ct-editor"
        ref={this.root}
        onPointerDown={this.onPointerDown}
        onPointerMove={this.onPointerMove}
        onPointerUp={this.onPointerUp}
        onPointerCancel={this.onPointerCancel}
      >
        <div className="ct-sec">
          <div className="ct-sec-left">
            <h5 className="fw-bold text-dark mb-0">{t("checklist_items")}</h5>
            {headerExtra}
          </div>
          <div className="ct-addgrp">
            <button type="button" className="ct-add sec" onClick={() => this.addSection(null)}>
              {t("ct_add_section")}
            </button>
            <button type="button" className="ct-add" onClick={() => this.addItem(null)}>
              {t("ct_add_item")}
            </button>
          </div>
        </div>
        <p className="ct-dndhint">{t("ct_dnd_hint")}</p>
        <div className="ct-items">{this.renderList(tree, "")}</div>
        <div className="ct-sr" aria-live="polite">
          {this.state.live}
        </div>
        {this.renderMoveBar()}
      </div>
    );
  }
}
